import React from "react";
import { Card, Modal } from "react-bootstrap";
import IconTable from "components/IconTable";
import JSONPretty from "react-json-pretty";
import { isEmpty } from "lodash";
import CsvDownloadLink from "components/CsvDownloadLink";
import { FiDownload } from "react-icons/fi";
import IconTableHeader from "components/IconTableHeader";

// Cell values are rendered into the CSV (CsvCell[][]) and table cells.
type Cell = string | number | boolean | null | undefined;

interface PaychexMetadata {
  givenName?: Cell;
  surname?: Cell;
  dateOfBirth?: Cell;
  emailAddress?: Cell;
  rothBalance?: Cell;
  preTaxBalance?: Cell;
  [key: string]: unknown;
}

interface PaychexUser {
  accountCreatedDate?: Cell;
  currentStateTranslation?: Cell;
  rolloverStatus?: Cell;
  rolloverAmount?: Cell;
  rolloverDepositDate?: Cell;
  accountEmail?: Cell;
  userId?: Cell;
  stateMachine?: Cell;
  accountType?: Cell;
  accountState?: Cell;
  accountStateTrigger?: Cell;
  accountStateCreatedAt?: Cell;
  paychexRothBalance?: Cell;
  paychexTraditionalBalance?: Cell;
  paychexMetadata?: PaychexMetadata;
}

interface Props {
  paychexUsersData: PaychexUser[];
  totalPaychexUsers?: number;
}

const UserDataTable = ({ paychexUsersData, totalPaychexUsers }: Props) => {
  const data = [
    [
      "Account Created Date",
      "Current State",
      "Rollover Status",
      "Rollover Amount",
      "Rollover Deposit Date",
      "Account Email",
      "User ID",
      "State Machine",
      "Account Type",
      "Account State",
      "Account State Trigger",
      "Account State Created At",
      "Paychex Roth Balance",
      "Paychex Traditional Balance",
      "Given Name",
      "Surname",
      "Date of Birth",
      "Email Address",
      "Roth Balance",
      "Pre-Tax Balance",
    ],
    ...paychexUsersData.map((user) => {
      const { paychexMetadata = {} } = user;
      return [
        user.accountCreatedDate,
        user.currentStateTranslation,
        user.rolloverStatus,
        user.rolloverAmount,
        user.rolloverDepositDate,
        user.accountEmail,
        user.userId,
        user.stateMachine,
        user.accountType,
        user.accountState,
        user.accountStateTrigger,
        user.accountStateCreatedAt,
        user.paychexRothBalance,
        user.paychexTraditionalBalance,
        paychexMetadata.givenName,
        paychexMetadata.surname,
        paychexMetadata.dateOfBirth,
        paychexMetadata.emailAddress,
        paychexMetadata.rothBalance,
        paychexMetadata.preTaxBalance,
      ];
    }),
  ];

  return (
    <Card>
      <Card.Body>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <CsvDownloadLink data={data} filename={"paychex_users.csv"}>
            <div className="downloader-description">
              <FiDownload
                fill={"#FFFFFF"}
                color={"#FFFFFF"}
                stroke={"#29b0c2"}
                size={"24px"}
              />
            </div>
          </CsvDownloadLink>
        </div>
        <IconTableHeader
          tableHeader="Paychex Users"
          tableCount={totalPaychexUsers}
        />
        <IconTable<PaychexUser>
          columns={[
            { label: "Account Created Date", key: "accountCreatedDate" },
            { label: "Current State", key: "currentStateTranslation" },
            { label: "Rollover Status", key: "rolloverStatus" },
            {
              label: "Rollover Amount",
              key: "rolloverAmount",
            },
            { label: "Rollover Deposit Date", key: "rolloverDepositDate" },
            { label: "Account Email", key: "accountEmail" },
            { label: "User ID", key: "userId" },

            { label: "State Machine", key: "stateMachine" },
            { label: "Account Type", key: "accountType" },
            { label: "Account State", key: "accountState" },
            { label: "Account State Trigger", key: "accountStateTrigger" },
            { label: "Account State Created At", key: "accountStateCreatedAt" },
            {
              label: "Paychex Roth Balance",
              key: "paychexRothBalance",
            },
            {
              label: "Paychex Traditional Balance",
              key: "paychexTraditionalBalance",
            },
            {
              label: "Paychex Metadata",
              key: "paychexMetadata",
              customComponent: (value) => {
                if (isEmpty(value.paychexMetadata)) {
                  return null;
                }
                // found-not-fixed: useState is called conditionally (after the
                // early return) inside this render callback — a pre-existing
                // rules-of-hooks violation; preserved as-is.
                const [showModal, setShowModal] = React.useState(false);
                return (
                  <>
                    <a
                      className="link"
                      style={{
                        paddingLeft: 0,
                      }}
                      onClick={() => setShowModal(true)}
                    >
                      View Metadata
                    </a>

                    <Modal
                      show={showModal}
                      onHide={() => setShowModal(false)}
                      size="lg"
                    >
                      <Modal.Header closeButton>
                        <Modal.Title>Paychex Metadata</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <JSONPretty
                          data={value.paychexMetadata}
                          themeClassName="custom-json-pretty"
                          style={{ fontSize: "14px", margin: 15 }}
                          mainStyle="background:#F7F7F7; line-height:1.3; padding: 20px; ;overflow:auto; color:#999;"
                          keyStyle="color:#905;"
                          stringStyle="color:#690;"
                          valueStyle="color:#905;"
                          booleanStyle="color:#905;"
                        ></JSONPretty>
                      </Modal.Body>
                    </Modal>
                  </>
                );
              },
            },
          ]}
          data={paychexUsersData}
        />
      </Card.Body>
    </Card>
  );
};

export default UserDataTable;
